import { gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type DeleteJournalLineInput = {
  journalEntryId: string;
  journalLineId: string;
};

const DELETE_JOURNAL_LINE_MUTATION = gql`
  mutation DeleteJournalLine(
    $journalEntryId: String!
    $journalLineId: String!
  ) {
    deleteJournalLine(
      journalEntryId: $journalEntryId
      journalLineId: $journalLineId
    )
  }
`;

export const useDeleteJournalLine = () => {
  const { t } = useTranslation();
  const [deleteJournalLine, { data, loading, error }] = useMutation(
    DELETE_JOURNAL_LINE_MUTATION,
    {
      refetchQueries: ["GetAllJournalEntries"],
    },
  );

  const handleDeleteJournalLine = async (
    data: DeleteJournalLineInput,
    onSuccess: (data: boolean) => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await deleteJournalLine({
        variables: {
          journalEntryId: data.journalEntryId,
          journalLineId: data.journalLineId,
        },
      });

      if (response.errors) {
        throw new Error("error deleting journal line");
      }

      onSuccess(response.data.deleteJournalLine);

      toast.dismiss(toastId);

      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleDeleteJournalLine, { data, loading, error }] as const;
};
